import React, { useRef, useEffect } from "react";
import { Box, CircularProgress, Alert } from "@mui/material";
import { useToast } from "../../contexts/ToastContext";
import { useHeaderPageHeading } from "../../contexts/HeaderContext";
import { colors } from "../../styles/theme";
import { SeitenTitelDaten, useSeitenTitel } from "./SeitenTitelContext";

interface PageTemplateProps {
  /** Ladezustand – zeigt zentrierten Spinner statt Content */
  isLoading?: boolean;
  /** Blockierender Fehler – zeigt Alert statt Content (z.B. Ladefehler ohne Daten) */
  error?: string | null;
  /** Speicher-Indikator (true während API-Call) */
  isSaving?: boolean;
  /** Fehlermeldung aus dem letzten Speichervorgang – wird als Toast angezeigt */
  saveError?: string | null;
  /**
   * Überschreibt den Titel aus der Navigation – nur für Ansichten, die die Navigation nicht
   * kennt (ein geöffneter Datensatz, z. B. ein Nachweis). Sonst weglassen.
   */
  seitenTitel?: SeitenTitelDaten;
  children: React.ReactNode;
}

/**
 * Einheitliches Seiten-Template für alle Projekt-Detailseiten.
 * Stellt konsistente Darstellung von Lade-, Fehler- und Speicherzuständen sicher.
 *
 * Die Überschrift der Seite steht im Header über der Inhaltsspalte, nicht hier – so beginnt der
 * Inhalt direkt oben. Sie kommt aus der Navigation (`SeitenTitelContext`), jede Seite hat sie
 * also automatisch; `seitenTitel` überschreibt sie für einen geöffneten Datensatz.
 *
 * Speicher-Feedback läuft zentral über Toast-Benachrichtigungen:
 * - isSaving true→false ohne neuen saveError → Erfolgs-Toast
 * - isSaving true→false mit neuem saveError   → Fehler-Toast
 */
const PageTemplate: React.FC<PageTemplateProps> = ({
  isLoading = false,
  error,
  isSaving = false,
  saveError,
  seitenTitel,
  children,
}) => {
  const ausNavigation = useSeitenTitel();
  useHeaderPageHeading(seitenTitel ?? ausNavigation);
  const prevIsSaving = useRef(false);
  const saveErrorWhenStarted = useRef<string | null>(null);
  const { showSuccess, showError: toastError } = useToast();

  useEffect(() => {
    if (isSaving && !prevIsSaving.current) {
      saveErrorWhenStarted.current = saveError ?? null;
    }

    if (!isSaving && prevIsSaving.current) {
      const currentSaveError = saveError ?? null;
      if (
        currentSaveError &&
        currentSaveError !== saveErrorWhenStarted.current
      ) {
        toastError(currentSaveError);
      } else {
        showSuccess("Gespeichert");
      }
    }

    prevIsSaving.current = isSaving;
  }, [isSaving, saveError, showSuccess, toastError]);

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box
      sx={{ position: "relative", minHeight: isLoading ? "400px" : undefined }}
    >
      {isLoading && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            zIndex: 1,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: colors.glass.backgroundStrong,
          }}
        >
          <CircularProgress />
        </Box>
      )}
      <Box>{children}</Box>
    </Box>
  );
};

export default PageTemplate;
